import {RefObject, useCallback, useState} from 'react';

function isClipboardMimeTypeSupported(mimeType: string): boolean {
  if (typeof ClipboardItem === 'undefined') return false;
  const clipboardItemSupports = (ClipboardItem as unknown as {supports?: (type: string) => boolean}).supports;
  if (typeof clipboardItemSupports === 'function') {
    return clipboardItemSupports(mimeType);
  }

  // Heuristic: PNG is widely supported; JPEG often isn't for clipboard writes.
  return mimeType === 'image/png';
}

async function copySvgElementAsImage(svgElement: SVGElement, format: 'jpeg' | 'png'): Promise<void> {
  if (!svgElement) {
    throw new Error('SVG element not found');
  }

  // Get the actual dimensions of the SVG
  const rect = svgElement.getBoundingClientRect();
  let {width} = rect;
  let {height} = rect;

  // Fallback to SVG attributes if bounding rect is 0
  if (width === 0 || height === 0) {
    width = parseInt(svgElement.getAttribute('width') || '0', 10);
    height = parseInt(svgElement.getAttribute('height') || '0', 10);
  }

  // Final fallback - use viewBox if available
  if (width === 0 || height === 0) {
    const viewBox = svgElement.getAttribute('viewBox');
    if (viewBox) {
      const [, , vw, vh] = viewBox.split(/\s+|,/).map(Number);
      if (vw && vh) {
        width = vw;
        height = vh;
      }
    }
  }

  if (width === 0 || height === 0) {
    throw new Error('SVG has no dimensions');
  }

  // Clone the SVG to avoid modifying the original
  const clonedSvg = svgElement.cloneNode(true) as SVGElement;

  // Ensure explicit width and height are set
  clonedSvg.setAttribute('width', width.toString());
  clonedSvg.setAttribute('height', height.toString());

  // Ensure viewBox is set if not present
  if (!clonedSvg.getAttribute('viewBox')) {
    clonedSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  // Get SVG as string
  const svgData = new XMLSerializer().serializeToString(clonedSvg);
  const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
  const svgUrl = URL.createObjectURL(svgBlob);

  // Create an image to load the SVG
  const img = new Image();
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  return new Promise<void>((resolve, reject) => {
    img.onload = () => {
      try {
        // Set canvas dimensions to match SVG
        canvas.width = width;
        canvas.height = height;

        // Draw white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Convert canvas to blob
        const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
        const clipboardType = format === 'jpeg' ? 'image/jpeg' : 'image/png';

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            // Copy to clipboard
            navigator.clipboard
              .write([
                new ClipboardItem({
                  [clipboardType]: blob,
                }),
              ])
              .then(() => {
                URL.revokeObjectURL(svgUrl);
                resolve();
              })
              .catch(reject);
          },
          mimeType,
          format === 'jpeg' ? 0.95 : undefined,
        );
      } catch (error) {
        URL.revokeObjectURL(svgUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error('Failed to load SVG image'));
    };

    img.src = svgUrl;
  });
}

export function useCopyGraphAsImage(ref: RefObject<HTMLElement | null>) {
  const [copying, setCopying] = useState(false);

  const copyGraphAsImage = useCallback(
    async (format: 'jpeg' | 'png' = 'png') => {
      if (!ref.current) return;

      // Find the main SVG element - Observable Plot creates it as a direct child
      // Look for the largest SVG element (the main chart, not nested elements)
      const allSvgs = Array.from(ref.current.querySelectorAll('svg')) as SVGElement[];
      if (allSvgs.length === 0) {
        throw new Error('SVG element not found');
      }

      // Get the main chart SVG (usually the largest one)
      const svgElement = allSvgs.reduce((largest, current) => {
        const largestRect = largest.getBoundingClientRect();
        const currentRect = current.getBoundingClientRect();
        return currentRect.width * currentRect.height > largestRect.width * largestRect.height ? current : largest;
      });

      const requestedFormat = format;
      const effectiveFormat =
        requestedFormat === 'jpeg' && !isClipboardMimeTypeSupported('image/jpeg') ? 'png' : requestedFormat;

      setCopying(true);
      try {
        await copySvgElementAsImage(svgElement, effectiveFormat);
      } catch (error) {
        // Fallback: try to copy as PNG if JPG fails and format was jpeg
        if (effectiveFormat === 'jpeg') {
          try {
            await copySvgElementAsImage(svgElement, 'png');
          } catch (fallbackError) {
            console.error('Fallback copy also failed:', fallbackError);
            throw fallbackError;
          }
        } else {
          console.error(`Error copying graph as ${effectiveFormat.toUpperCase()}:`, error);
          throw error;
        }
      } finally {
        setCopying(false);
      }
    },
    [ref],
  );

  return {copyGraphAsImage, copying};
}
